import { appRoutes } from '@/appRoutes';

import {
  ChatIcon,
  ContactsIcon,
  JobsIcon,
  MeIcon,
  QuickTasksIcon,
  ReportingIcon,
  SettingsIcon,
} from './icons';
import { NavItem } from './types';

import type {
  ContactsRoutes,
  ChatRoutes,
  JobsRoutes,
  MyListsRoutes,
  MyPerformanceRoutes,
  MyTasksRoutes,
  MyTimeRoutes,
  PerformanceRoutes,
  QuickTasksRoutes,
  SettingsRoutes,
} from '@/app/(protected)/[workspace_slug]/routes';
import type { SettingsPageTabsAccess } from '@/app/(protected)/[workspace_slug]/settings/pageTabs';

type GetWorkspaceNavItemsOptions = {
  settingsAccess?: SettingsPageTabsAccess;
  hasPremiumAccess?: boolean;
  hasUnreadChat?: boolean;
};

// * This is the netnet app navigation items for the workspace.

export function getWorkspaceNavItems(
  workspaceSlug: string,
  options?: GetWorkspaceNavItemsOptions,
): NavItem[] {
  const contactsRoutes = appRoutes.workspace.contacts(
    workspaceSlug,
  ) as ContactsRoutes;
  const chatRoutes = appRoutes.workspace.chat(workspaceSlug) as ChatRoutes;
  const jobsRoutes = appRoutes.workspace.jobs(workspaceSlug) as JobsRoutes;
  const quickTasksRoutes = appRoutes.workspace.quickTasks(
    workspaceSlug,
  ) as QuickTasksRoutes;
  const myListsRoutes = appRoutes.workspace.myLists(
    workspaceSlug,
  ) as MyListsRoutes;
  const myTasksRoutes = appRoutes.workspace.myTasks(
    workspaceSlug,
  ) as MyTasksRoutes;
  const myTimeRoutes = appRoutes.workspace.myTime(
    workspaceSlug,
  ) as MyTimeRoutes;
  const myPerformanceRoutes = appRoutes.workspace.myPerformance(
    workspaceSlug,
  ) as MyPerformanceRoutes;
  const performanceRoutes = appRoutes.workspace.performance(
    workspaceSlug,
  ) as PerformanceRoutes;
  const settingsRoutes = appRoutes.workspace.settings(
    workspaceSlug,
  ) as SettingsRoutes;
  const subscriptionHref = settingsRoutes.subscription.index;

  const settingsChildren = [
    {
      key: 'settings-team',
      label: 'Team',
      href: settingsRoutes.team.index,
      sectionKey: 'team',
    },
    {
      key: 'settings-service-types',
      label: 'Service Types',
      href: settingsRoutes.serviceTypes.index,
      sectionKey: 'service-types',
    },
    {
      key: 'settings-deliverable-types',
      label: 'Deliverable Types',
      href: settingsRoutes.deliverableTypes.index,
      sectionKey: 'deliverable-types',
    },
    {
      key: 'settings-workspace',
      label: 'Workspace',
      href: settingsRoutes.workspace.index,
      sectionKey: 'workspace',
    },
    {
      key: 'settings-subscription',
      label: 'Subscription',
      href: settingsRoutes.subscription.index,
      sectionKey: 'subscription',
    },
    {
      key: 'settings-terms-and-conditions',
      label: 'Terms & Conditions',
      href: settingsRoutes.termsAndConditions.index,
      sectionKey: 'terms-and-conditions',
    },
  ] as const;

  const allowedSettingsChildren = settingsChildren.filter(
    (child) => options?.settingsAccess?.[child.sectionKey] !== false,
  );

  const navItems: NavItem[] = [
    {
      key: 'me',
      label: 'Me',
      iconIdle: <MeIcon />,
      iconHover: <MeIcon active />,
      defaultHref: myTasksRoutes.index,
      children: [
        {
          key: 'my-tasks',
          label: 'My Tasks',
          href: myTasksRoutes.index,
        },
        {
          key: 'my-lists',
          label: 'My Lists',
          href: myListsRoutes.index,
        },
        {
          key: 'my-time',
          label: 'My Time',
          href: myTimeRoutes.index,
        },
        {
          key: 'my-performance',
          label: 'My Performance',
          href: myPerformanceRoutes.index,
        },
      ],
    },
    {
      key: 'contacts',
      label: 'Contacts',
      href: contactsRoutes.index,
      iconIdle: <ContactsIcon />,
      iconHover: <ContactsIcon active />,
    },
    {
      key: 'jobs',
      label: 'Jobs',
      href: jobsRoutes.index,
      iconIdle: <JobsIcon />,
      iconHover: <JobsIcon active />,
    },
    {
      key: 'quick-tasks',
      label: 'Quick Tasks',
      href: quickTasksRoutes.index,
      iconIdle: <QuickTasksIcon />,
      iconHover: <QuickTasksIcon active />,
    },
    {
      key: 'chat',
      label: 'Chat',
      href: chatRoutes.index,
      iconIdle: <ChatIcon />,
      iconHover: <ChatIcon active />,
      showIndicator: options?.hasUnreadChat === true,
      indicatorLabel: 'Unread chat messages',
      disabled: options?.hasPremiumAccess === false,
      disabledHref: subscriptionHref,
    },
    {
      key: 'performance',
      label: 'Performance',
      iconIdle: <ReportingIcon />,
      iconHover: <ReportingIcon active />,
      defaultHref: performanceRoutes.reports.time,
      children: [
        {
          key: 'performance-reports-time',
          label: 'Time',
          href: performanceRoutes.reports.time,
        },
        {
          key: 'performance-reports-team',
          label: 'Team',
          href: performanceRoutes.reports.team,
        },
        {
          key: 'performance-reports-jobs',
          label: 'Jobs',
          href: performanceRoutes.reports.jobs,
        },
      ],
    },
    {
      key: 'settings',
      label: 'Settings',
      iconIdle: <SettingsIcon />,
      iconHover: <SettingsIcon active />,
      layoutGroup: 'secondary',
      defaultHref: allowedSettingsChildren[0]?.href,
      children: allowedSettingsChildren.map(
        ({ sectionKey: _sectionKey, ...child }) => child,
      ),
    },
  ];

  if (!allowedSettingsChildren.length) {
    return navItems.filter((item) => item.key !== 'settings');
  }

  return navItems;
}
